// ABOUTME: The Too Much ring -- four quarter-slices around the canoe, each opening to its pills when tapped.
// ABOUTME: Presentation only: reports taps upward, owns no save call and no content fetch (Issue #1944); v28 geometry from the Reset prototype.
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { useCrossingReducedMotion } from './crossing/useReducedMotion';
import { useFrameScale } from './crossing/useFrameScale';
import { useViewportWidth } from './runner/useViewportWidth';
import {
  CROSSING_PILL_BG, CROSSING_PILL_BORDER, CROSSING_PILL_INK,
  TOOMUCH_B_BG, TOOMUCH_B_BORDER, TOOMUCH_B_INK, TOOMUCH_B_DARK,
  TOOMUCH_A_BG, TOOMUCH_A_BORDER, TOOMUCH_A_INK, TOOMUCH_A_DARK,
  TOOMUCH_S_BG, TOOMUCH_S_BORDER, TOOMUCH_S_INK, TOOMUCH_S_DARK,
  TOOMUCH_F_BG, TOOMUCH_F_BORDER, TOOMUCH_F_INK, TOOMUCH_F_DARK,
} from '../../theme/crossingTokens';
import type { CrossingTooMuchCategoryDto } from '../../types/api';

// Exactly these 4 codes render, in this order -- CrossingTooMuchCategory also seeds a 5th
// `not_sure` row (S19 content), but nothing in the confirmed-live ring design renders it
// (spec CONFLICTS #4, bryan-confirmed). An unrecognized code in `categories` (not_sure or any
// future addition) is simply skipped: there is no fallback color/geometry to give it.
const PETAL_CODES = ['body_needs', 'too_much_at_once', 'senses_full', 'feelings_piled_up'] as const;

// The ring is a 312 px box of four 148 px slices (R 82, r 13, outer 134), scaled up so the canoe's whole rock stays inside the centre circle.
const RING_BOX = 312;
const SLICE_SIZE = 148;
const CENTRE_RADIUS = 82;
const DESKTOP_SCALE = 1.48;
// On a phone the ring grows as far as the screen allows: its four slices span 356 px, with 8 px to spare on each side.
const PHONE_SCALE_MAX = 1.05;
const RING_EXTENT_W = 356;
const PHONE_SIDE_MARGIN = 8;
// Only the phone/desktop switch is read from the frame scale, so any authored width will do.
const FRAME_WIDTH = 360;
const SLICE_STAGGER_MS = 80;

// One annular quarter, mirrored into its corner: mx/my are the horizontal and vertical mirrors, (cx, cy) the circle's centre.
function ringPath(mx: 1 | -1, my: 1 | -1, cx: number, cy: number): string {
  const R = CENTRE_RADIUS;
  const r = 13;
  const outer = 134;
  const ya = cy - Math.sqrt(R * R - (cx - 148) * (cx - 148));
  const xa = cx - Math.sqrt(R * R - (cy - 148) * (cy - 148));
  const T = (x: number, y: number) => `${(mx > 0 ? x : 148 - x).toFixed(1)} ${(my > 0 ? y : 148 - y).toFixed(1)}`;
  const sweep = mx * my > 0 ? [1, 0] : [0, 1];
  return `M${T(outer, 0)} L${T(148 - r, 0)} A${r} ${r} 0 0 ${sweep[0]} ${T(148, r)} L${T(148, ya)} A${R} ${R} 0 0 ${sweep[1]} ${T(xa, 148)} L${T(r, 148)} A${r} ${r} 0 0 ${sweep[0]} ${T(0, 148 - r)} L${T(0, outer)} A${outer} ${outer} 0 0 ${sweep[0]} ${T(outer, 0)} Z`;
}

type Corner = 'tl' | 'tr' | 'bl' | 'br';

// Each slice slides in from its own corner: the same 150 px offset and 0.92 scale the prototype starts from.
const CORNER_OFFSET: Record<Corner, [number, number]> = {
  tl: [-150, -150], tr: [150, -150], bl: [-150, 150], br: [150, 150],
};
const offsetTransform = (corner: Corner) => `translate(${CORNER_OFFSET[corner][0]}px, ${CORNER_OFFSET[corner][1]}px) scale(0.92)`;
// `from` only, so each animation ends at the slice's own style (opacity 0.38 for a dimmed slice).
const slideFrom = (corner: Corner) => keyframes`
  from { transform: ${offsetTransform(corner)}; }
`;
const SLIDE_FROM: Record<Corner, ReturnType<typeof keyframes>> = {
  tl: slideFrom('tl'), tr: slideFrom('tr'), bl: slideFrom('bl'), br: slideFrom('br'),
};
const FADE_FROM = keyframes`
  from { opacity: 0; }
`;

interface SliceGeometry {
  corner: Corner;
  left: string;
  top: string;
  path: string;
  idlePadding: string;
  openPadding: string;
  bg: string;
  border: string;
  ink: string;
  dark: string;
}

const GEOMETRY: Record<(typeof PETAL_CODES)[number], SliceGeometry> = {
  body_needs: {
    corner: 'tl', left: '-22px', top: '-34px', path: ringPath(1, 1, 178, 190), idlePadding: '0 10px 10px 0', openPadding: '24px 40px 30px 24px',
    bg: TOOMUCH_B_BG, border: TOOMUCH_B_BORDER, ink: TOOMUCH_B_INK, dark: TOOMUCH_B_DARK,
  },
  too_much_at_once: {
    corner: 'tr', left: '186px', top: '-34px', path: ringPath(-1, 1, 178, 190), idlePadding: '0 0 10px 10px', openPadding: '24px 24px 30px 40px',
    bg: TOOMUCH_A_BG, border: TOOMUCH_A_BORDER, ink: TOOMUCH_A_INK, dark: TOOMUCH_A_DARK,
  },
  senses_full: {
    corner: 'bl', left: '-22px', top: '170px', path: ringPath(1, -1, 178, 162), idlePadding: '10px 10px 0 0', openPadding: '30px 40px 24px 24px',
    bg: TOOMUCH_S_BG, border: TOOMUCH_S_BORDER, ink: TOOMUCH_S_INK, dark: TOOMUCH_S_DARK,
  },
  feelings_piled_up: {
    corner: 'br', left: '186px', top: '170px', path: ringPath(-1, -1, 178, 162), idlePadding: '10px 0 0 10px', openPadding: '30px 24px 24px 40px',
    bg: TOOMUCH_F_BG, border: TOOMUCH_F_BORDER, ink: TOOMUCH_F_INK, dark: TOOMUCH_F_DARK,
  },
};

interface TooMuchQuadrantProps {
  categories: Record<string, CrossingTooMuchCategoryDto>;
  focusedCode: string | null;
  pickedLabels: string[];
  onPetalTap: (code: string) => void;
  onPillTap: (label: string) => void;
  fading?: boolean; // the slices fade out and take no input (Breathing strategy, Ready for compass)
  fadeSeconds?: number;
  // waiting: the slices are up but not yet visible, at their corner offset; arriving: they slide and fade in; up: they are simply there
  entrance?: 'waiting' | 'arriving' | 'up';
}

export default function TooMuchQuadrant({ categories, focusedCode, pickedLabels, onPetalTap, onPillTap, fading = false, fadeSeconds = 0.6, entrance = 'up' }: TooMuchQuadrantProps) {
  const reducedMotion = useCrossingReducedMotion();
  const { mob } = useFrameScale(FRAME_WIDTH);
  const viewportWidth = useViewportWidth();
  const scale = mob ? Math.min(PHONE_SCALE_MAX, (viewportWidth - 2 * PHONE_SIDE_MARGIN) / RING_EXTENT_W) : DESKTOP_SCALE;
  return (
    <Box
      data-testid="too-much-quadrant"
      data-centre-radius={CENTRE_RADIUS * scale}
      inert={fading}
      style={{ transform: `translate(-50%, -50%) scale(${scale})` }}
      sx={{ position: 'absolute', left: '50%', top: '50%', width: `${RING_BOX}px`, height: `${RING_BOX}px`, pointerEvents: 'none' }}
    >
      {PETAL_CODES.map((code, index) => {
        const category = categories[code];
        if (!category) return null; // content not loaded yet for this code -- render nothing, not a placeholder
        const geo = GEOMETRY[code];
        const isFocused = focusedCode === code;
        const dimmed = focusedCode !== null && !isFocused;
        return (
          // Plain (non-button) container carrying the slice's visual styling -- its title button
          // and pill buttons below are SIBLINGS, never nested, since a <button> inside another
          // <button> is invalid HTML and an accessibility anti-pattern (code review finding).
          <Box
            key={code}
            data-testid={`too-much-petal-${code}`}
            data-corner={geo.corner}
            style={{
              opacity: fading || entrance === 'waiting' ? 0 : dimmed ? 0.38 : 1,
              transform: entrance === 'waiting' ? offsetTransform(geo.corner) : 'none',
              transition: reducedMotion ? 'none' : fading ? `opacity ${fadeSeconds}s ease` : `opacity 0.7s ease ${index * SLICE_STAGGER_MS}ms`,
              pointerEvents: fading ? 'none' : 'auto',
            }}
            sx={{
              position: 'absolute', left: geo.left, top: geo.top, width: `${SLICE_SIZE}px`, height: `${SLICE_SIZE}px`,
              boxSizing: 'border-box', padding: isFocused ? geo.openPadding : geo.idlePadding,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: isFocused ? '3px' : '5px',
              isolation: 'isolate', color: geo.dark, filter: `drop-shadow(0 3px 6px ${alpha(CROSSING_PILL_INK, 0.14)})`,
              animationName: reducedMotion || entrance !== 'arriving' ? 'none' : `${SLIDE_FROM[geo.corner]}, ${FADE_FROM}`,
              animationDuration: '0.9s, 0.7s', animationTimingFunction: 'cubic-bezier(.2,.7,.2,1), ease',
              animationDelay: `${index * SLICE_STAGGER_MS}ms, ${index * SLICE_STAGGER_MS}ms`, animationFillMode: 'backwards',
            }}
          >
            <svg viewBox={`0 0 ${SLICE_SIZE} ${SLICE_SIZE}`} width={SLICE_SIZE} height={SLICE_SIZE} aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, zIndex: -1, overflow: 'visible' }}>
              <path
                d={geo.path}
                fill={geo.bg}
                stroke={isFocused ? geo.ink : geo.border}
                strokeWidth={1.5}
                style={{ transition: reducedMotion ? 'none' : 'fill 0.35s ease, stroke 0.35s ease' }}
              />
            </svg>
            <Box
              component="button"
              type="button"
              aria-pressed={isFocused}
              aria-label={category.title.replace(/\n/g, ' ')}
              onClick={() => onPetalTap(code)}
              sx={{
                flex: isFocused ? 'none' : 1,
                width: '100%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: '5px',
                background: 'none', border: 'none', padding: 0, margin: 0, color: 'inherit',
                cursor: 'pointer', font: 'inherit',
              }}
            >
              {!isFocused && (
                <>
                  <Box sx={{ fontSize: '17px', lineHeight: 1, color: geo.ink }}>{category.icon}</Box>
                  <Box sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.3px', textAlign: 'center', lineHeight: 1.5, whiteSpace: 'pre-line', color: geo.dark }}>{category.title}</Box>
                </>
              )}
              {isFocused && (
                <Box sx={{ fontSize: '7.5px', fontWeight: 800, letterSpacing: '1.1px', whiteSpace: 'nowrap', lineHeight: 1, color: geo.dark }}>{category.title.replace(/\n/g, ' ')}</Box>
              )}
            </Box>
            {isFocused && category.supports.map(support => {
              const isPicked = pickedLabels.includes(support.label);
              return (
                <Box
                  key={support.code}
                  component="button"
                  type="button"
                  aria-pressed={isPicked}
                  onClick={() => onPillTap(support.label)}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    background: isPicked ? geo.ink : CROSSING_PILL_BG,
                    border: `1.2px solid ${isPicked ? geo.ink : CROSSING_PILL_BORDER}`,
                    color: isPicked ? CROSSING_PILL_BG : geo.dark,
                    fontSize: '8.5px', fontWeight: 800, borderRadius: '999px', padding: '2.5px 8px', cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isPicked ? '✓ ' : ''}{support.label}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}
